"use client";

import { useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import dynamic from "next/dynamic";
import Link from "@/components/ui/intent-link";
import {
  Accessibility,
  ArrowLeft,
  ArrowUpRight,
  Bus,
  Car,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CtaButton } from "@/components/optilase/cta-button";
import { RichText, richTextHasContent, type RichTextValue } from "@/components/rich-text";
import { regionFromPathname } from "@/lib/region";
import { richParagraphs } from "@/lib/richtext";
import { CountUp } from "@/components/optilase/count-up";
import type { Clinic, ServiceLink } from "@/lib/clinics";
import { useVerticalScroll } from "@/lib/use-scrollable";
import { cn } from "@/lib/utils";
import { NearViewport } from "@/components/ui/near-viewport";
import { usePublicPathname } from "@/lib/public-pathname";

function TravelCardBody({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const { overflows, needsFocus } = useVerticalScroll(ref);

  return (
    <div className="relative mt-2">
      <div
        ref={ref}
        tabIndex={needsFocus ? 0 : undefined}
        aria-label={needsFocus ? "Scrollable details" : undefined}
        className="max-h-56 scrollbar-none overflow-y-auto"
      >
        {children}
        {overflows && <div aria-hidden className="h-10" />}
      </div>
      {overflows && (
        <div
          aria-hidden
          className="from-teal-02 pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t to-transparent"
        />
      )}
    </div>
  );
}

const ClinicMap = dynamic(() => import("@/components/bloks/clinic-map"), {
  ssr: false,
  loading: () => <div className="bg-teal-01 size-full animate-pulse" />,
});

type Stat = { number: string; label: string };
type Hours = { day: string; time: string };
type Parking = { car: string; transport: string[]; accessibility: string };

export type ClinicPageViewProps = {
  clinic: Clinic;
  heroButton: { label: string; href: string | null; target?: string };
  clinicsUrl: string;
  services: ServiceLink[];
  servicesImage: { filename: string; alt: string; focus?: string } | null;
  about: RichTextValue;
  stats: Stat[];
  hours: Hours[];
  parking: Parking;
  locationNote?: string;
  aboutImage?: { filename: string; alt: string; focus?: string } | null;
};

function CheckCircle({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={`shrink-0 ${className ?? ""}`}
    >
      <circle cx="10" cy="10" r="9.1" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 10.4l2.6 2.6 5.4-5.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const CLINIC_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Dublin",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
function clinicClock(): { day: string; minutes: number } {
  const parts = Object.fromEntries(
    CLINIC_CLOCK.formatToParts(new Date()).map((p) => [p.type, p.value]),
  );
  return {
    day: String(parts.weekday).toLowerCase(),
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}
const noopSubscribe = () => () => {};
function useTodayName(): string | null {
  return useSyncExternalStore(
    noopSubscribe,
    () => clinicClock().day,
    () => null,
  );
}

function dayCoversToday(dayLabel: string, todayName: string | null): boolean {
  if (!todayName) return false;
  const today = WEEKDAYS.indexOf(todayName);
  if (today === -1) return false;
  const indexOf = (s: string) => WEEKDAYS.findIndex((w) => s.trim().toLowerCase().startsWith(w));
  const parts = dayLabel.split(/[-–—]/);
  const start = indexOf(parts[0] ?? "");
  if (start === -1) return false;
  if (parts.length < 2) return start === today;
  const end = indexOf(parts[parts.length - 1] ?? "");
  if (end === -1) return start === today;
  return today >= Math.min(start, end) && today <= Math.max(start, end);
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-teal-07 text-[clamp(1.75rem,3vw,2.25rem)] leading-[1.12] font-bold">
      {children}
    </h2>
  );
}

function SectionNote({ children }: { children: React.ReactNode }) {
  return <p className="text-grey-06 mt-5 max-w-201.5 text-xl">{children}</p>;
}

const cardClass = "rounded-2xl border border-grey-01 bg-white p-7";
const cardHeading = "text-2xl font-bold text-teal-07";

export default function ClinicPageView({
  clinic,
  heroButton,
  clinicsUrl,
  services,
  servicesImage,
  about,
  stats,
  hours,
  parking,
  locationNote,
  aboutImage,
}: ClinicPageViewProps) {
  const todayName = useTodayName();
  const openNow = useMemo(() => {
    if (!todayName) return false;
    const today = hours.find((h) => dayCoversToday(h.day, todayName));
    const m = today?.time.match(/(\d{1,2}):(\d{2})\s*[—–-]\s*(\d{1,2}):(\d{2})/);
    if (!m) return false;
    const cur = clinicClock().minutes;
    return cur >= +m[1] * 60 + +m[2] && cur <= +m[3] * 60 + +m[4];
  }, [todayName, hours]);

  const aboutContent = richTextHasContent(about) ? about : clinic.summary;
  const region = regionFromPathname(usePublicPathname() ?? "/");
  const hasMap = clinic.lat != null && clinic.lng != null;
  const hasTravel = Boolean(parking.car || parking.transport.length > 0 || parking.accessibility);
  const servicePhoto = servicesImage ?? clinic.photo;
  const directionsUrl = hasMap
    ? `https://www.google.com/maps/dir/?api=1&destination=${clinic.lat},${clinic.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [clinic.name, clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", "),
      )}`;

  return (
    <>
      <section className="bg-white px-5 pt-10 pb-0 md:px-10 lg:px-20 lg:pt-20">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 lg:gap-6">
          {clinic.name && <h1 className="text-page-title text-teal-07">{clinic.name}</h1>}
          {clinic.tagline && (
            <p className="text-grey-07 text-xl font-semibold tracking-[-0.01em] lg:text-2xl">
              {clinic.tagline}
            </p>
          )}

          <div className="bg-teal-01 relative mt-1 aspect-video w-full overflow-hidden rounded-3xl sm:aspect-16/7">
            <AssetImage
              src={clinic.photo?.filename ?? ""}
              focus={clinic.photo?.focus}
              alt={clinic.photo?.alt || clinic.name}
              fill
              priority
              sizes="(min-width: 1115px) 1280px, 100vw"
              className="object-cover"
            />
            {clinic.flagship && (
              <span className="text-teal-08 absolute top-4 right-4 rounded-full bg-white px-3 py-1 text-xs font-medium tracking-wide uppercase">
                Primary
              </span>
            )}
          </div>

          {richTextHasContent(clinic.summary) && (
            <div className="mt-1 flex max-w-206.25 flex-col gap-4">
              {richParagraphs(clinic.summary, region).map((paragraph, i) => (
                <p key={i} className="text-grey-07 text-xl leading-normal">
                  {paragraph}
                </p>
              ))}
            </div>
          )}

          <CtaButton href={heroButton.href ?? undefined} variant="orange" className="w-fit">
            {heroButton.label}
          </CtaButton>
        </div>
      </section>

      <section className="bg-white px-5 pt-10 pb-10 md:px-10 lg:px-20 lg:pt-20 lg:pb-20">
        <div className="mx-auto w-full max-w-7xl">
          <section id="about" className="scroll-mt-28">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-15">
              <div className="lg:w-[calc(50%-1.875rem)] lg:shrink-0">
                <SectionHeading>About this clinic</SectionHeading>
                <RichText
                  value={aboutContent}
                  className="text-grey-07 mt-5 flex flex-col gap-4 text-base leading-relaxed"
                />
              </div>
              <div className="bg-teal-01 relative aspect-4/3 w-full overflow-hidden rounded-[20px] lg:aspect-16/11 lg:min-w-px lg:flex-1">
                <AssetImage
                  src={aboutImage?.filename ?? ""}
                  focus={aboutImage?.focus}
                  alt={aboutImage?.alt || clinic.name}
                  fill
                  sizes="(min-width: 1115px) 640px, 100vw"
                  className="object-cover"
                />
              </div>
            </div>

            {stats.length > 0 && (
              <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {stats.map((s, i) => (
                  <div key={i} className="bg-teal-01 rounded-2xl p-5">
                    <p className="text-teal-07 text-3xl leading-none font-bold lg:text-4xl">
                      <CountUp value={s.number} />
                    </p>
                    <p className="text-grey-06 mt-2 text-sm leading-snug">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section id="visiting" className="mt-16 scroll-mt-28 lg:mt-20">
            <SectionHeading>Visiting &amp; contact</SectionHeading>
            <SectionNote>Everything you need to plan your visit.</SectionNote>

            <div className="mt-10 grid gap-6 md:grid-cols-[minmax(0,1fr)_510px]">
              {hasMap && (
                <div className="relative">
                  <div className="bg-teal-01 isolate h-80 overflow-hidden rounded-2xl md:h-full md:min-h-105">
                    <NearViewport
                      className="size-full"
                      fallback={<div className="bg-teal-01 size-full" />}
                    >
                      <ClinicMap
                        pins={[{ clinic, num: 1 }]}
                        activeId={clinic.uuid}
                        hoveredId={null}
                        resetKey={0}
                        onSelect={() => {}}
                        markerVariant="logo"
                      />
                    </NearViewport>
                  </div>
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-07 hover:bg-teal-01 absolute bottom-3 left-3 z-1000 inline-flex h-10 items-center gap-2 rounded-full bg-white px-4 font-bold shadow-[0_6px_20px_-6px_rgba(5,29,45,0.3)] transition-colors"
                  >
                    Get directions
                    <ArrowUpRight className="size-5" />
                  </a>
                </div>
              )}

              <div className="flex flex-col gap-5">
                <div className={cardClass}>
                  <h3 className={cardHeading}>Contact</h3>
                  <div className="mt-4 flex flex-col gap-4">
                    {(clinic.addressLine1 || clinic.addressLine2) && (
                      <div className="flex gap-4">
                        <MapPin className="text-teal-07 mt-0.5 size-6 shrink-0" />
                        <p className="text-grey-07 text-base leading-relaxed">
                          {[clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    )}
                    {clinic.phone && (
                      <div className="flex gap-4">
                        <Phone className="text-teal-07 size-6 shrink-0" />
                        <a
                          href={`tel:${clinic.phone.replace(/\s+/g, "")}`}
                          className="text-grey-07 hover:text-teal-07 text-base transition-colors"
                        >
                          {clinic.phone}
                        </a>
                      </div>
                    )}
                    {clinic.email && (
                      <div className="flex gap-4">
                        <Mail className="text-teal-07 size-6 shrink-0" />
                        <a
                          href={`mailto:${clinic.email}`}
                          className="text-grey-07 hover:text-teal-07 text-base break-all transition-colors"
                        >
                          {clinic.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {locationNote && (
                  <div className={cardClass}>
                    <h3 className={cardHeading}>Clinic location</h3>
                    <p className="text-grey-07 mt-4 text-base leading-relaxed">{locationNote}</p>
                  </div>
                )}

                {hours.length > 0 && (
                  <div className={cardClass}>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className={cardHeading}>Opening hours</h3>
                      <span
                        className={cn(
                          "inline-flex h-7 items-center gap-2 rounded-full px-4 text-sm font-medium",
                          openNow ? "bg-teal-02 text-teal-08" : "bg-grey-01 text-grey-06",
                        )}
                      >
                        {openNow && <span className="bg-teal-07 size-2 rounded-full" />}
                        {openNow ? "Open" : "Closed"}
                      </span>
                    </div>
                    <ul className="mt-4 flex flex-col">
                      {hours.map((h, i) => {
                        const isToday = dayCoversToday(h.day, todayName);
                        return (
                          <li
                            key={i}
                            className={cn(
                              "border-grey-01 flex items-center justify-between border-t py-2 text-base leading-[1.08] first:border-t-0",
                              isToday ? "text-teal-07 font-bold" : "text-grey-07",
                            )}
                          >
                            <span>{h.day}</span>
                            <span>{h.time}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>

          {services.length > 0 && (
            <section id="services" className="mt-16 scroll-mt-28 lg:mt-20">
              <div className="flex flex-col gap-8 md:min-h-95 md:flex-row md:items-stretch md:gap-10 lg:gap-12">
                <div className="bg-teal-01 relative aspect-4/3 w-full overflow-hidden rounded-[20px] md:aspect-auto md:w-[44%] md:shrink-0">
                  <AssetImage
                    src={servicePhoto?.filename ?? ""}
                    focus={servicePhoto?.focus}
                    alt={servicePhoto?.alt || clinic.name}
                    fill
                    sizes="(min-width: 1115px) 460px, 100vw"
                    className="object-cover"
                  />
                </div>
                <div className="border-grey-01 flex flex-col items-center justify-center rounded-[20px] border bg-white p-6 text-center md:flex-1 lg:p-10">
                  <SectionHeading>Services offered here</SectionHeading>
                  <p className="text-grey-07 mt-4 text-base">
                    {services.length} service
                    {services.length === 1 ? "" : "s"} available at this clinic.
                  </p>
                  <ul className="mt-6 columns-1 gap-x-8 text-left sm:columns-2">
                    {services.map((s, i) =>
                      s.href ? (
                        <li key={i} className="mb-3 break-inside-avoid">
                          <Link
                            href={s.href}
                            target={s.target}
                            className="group flex items-start gap-3"
                          >
                            <CheckCircle className="text-teal-07 mt-0.5" />
                            <span className="text-grey-07 group-hover:text-teal-07 text-base transition-colors group-hover:underline">
                              {s.name}
                            </span>
                          </Link>
                        </li>
                      ) : (
                        <li key={i} className="mb-3 flex break-inside-avoid items-start gap-3">
                          <CheckCircle className="text-teal-07 mt-0.5" />
                          <span className="text-grey-07 text-base">{s.name}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </section>
          )}

          {(hasTravel || hasMap) && (
            <section id="travel" className="mt-16 scroll-mt-28 lg:mt-20">
              <SectionHeading>Getting here</SectionHeading>
              <SectionNote>How to find us, parking and accessibility.</SectionNote>
              <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {parking.car && (
                  <div className="bg-teal-02 rounded-xl p-6">
                    <span className="bg-teal-07 flex size-12 items-center justify-center rounded-full text-white">
                      <Car className="size-6" />
                    </span>
                    <h3 className="text-teal-08 mt-5 text-xl font-bold">By car</h3>
                    <TravelCardBody>
                      <p className="text-grey-07 text-base leading-relaxed">{parking.car}</p>
                    </TravelCardBody>
                  </div>
                )}
                {parking.transport.length > 0 && (
                  <div className="bg-teal-02 rounded-xl p-6">
                    <span className="bg-teal-07 flex size-12 items-center justify-center rounded-full text-white">
                      <Bus className="size-6" />
                    </span>
                    <h3 className="text-teal-08 mt-5 text-xl font-bold">Public transport</h3>
                    <TravelCardBody>
                      <ul className="flex flex-col gap-1.5">
                        {parking.transport.map((t, i) => (
                          <li key={i} className="text-grey-07 text-base leading-relaxed">
                            {t}
                          </li>
                        ))}
                      </ul>
                    </TravelCardBody>
                  </div>
                )}
                {parking.accessibility && (
                  <div className="bg-teal-02 rounded-xl p-6">
                    <span className="bg-teal-07 flex size-12 items-center justify-center rounded-full text-white">
                      <Accessibility className="size-6" />
                    </span>
                    <h3 className="text-teal-08 mt-5 text-xl font-bold">Accessibility</h3>
                    <TravelCardBody>
                      <p className="text-grey-07 text-base leading-relaxed">
                        {parking.accessibility}
                      </p>
                    </TravelCardBody>
                  </div>
                )}
                {hasMap && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group border-grey-01 hover:border-teal-04 flex flex-col rounded-xl border bg-white p-6 transition-colors"
                  >
                    <span className="bg-teal-07 flex size-12 items-center justify-center rounded-full text-white">
                      <MapPin className="size-6" />
                    </span>
                    <h3 className="text-teal-08 mt-5 text-xl font-bold">Get directions</h3>
                    <p className="text-grey-07 mt-2 text-base leading-relaxed">
                      Open Google Maps for turn-by-turn directions to this clinic.
                    </p>
                    <span className="text-teal-08 mt-auto inline-flex items-center gap-2 pt-4 font-bold group-hover:underline">
                      Open in Maps
                      <ArrowUpRight className="size-4" />
                    </span>
                  </a>
                )}
              </div>
            </section>
          )}

          <div className="mt-10">
            <Link
              href={clinicsUrl}
              className="text-teal-07 inline-flex w-fit items-center gap-2 text-base font-bold transition-colors hover:underline"
            >
              <ArrowLeft className="size-4.5" />
              Back to all clinics
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
